import React, { useState } from 'react';
import { FaSearch, FaMapMarkerAlt, FaSchool, FaUserTie } from 'react-icons/fa';
import SchoolResults from '../../components/public/SchoolResults';
import MentorResults from '../../components/public/MentorResults';
import { useToast } from '../../hooks/useToast';

/**
 * Public page for finding schools and mentors/experts
 */
const FindExpert = () => {
  const { showError } = useToast();
  
  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  const [searchType, setSearchType] = useState('all'); // 'all', 'schools', 'mentors'
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  
  // Location state
  const [useLocation, setUseLocation] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [locationLoading, setLocationLoading] = useState(false);
  
  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };
  
  // Handle search type change
  const handleSearchTypeChange = (type) => {
    setSearchType(type);
  };
  
  // Handle search form submission
  const handleSearch = (e) => {
    e.preventDefault();
    if (!searchTerm.trim()) return;
    
    setIsSearching(true);
    setHasSearched(true);

    // Simulate a search delay
    setTimeout(() => {
      setIsSearching(false);
    }, 500);
  };
  
  // Get user's location for location-based results
  const getLocation = () => {
    if (!navigator.geolocation) {
      showError('Geolocation is not supported by your browser');
      return;
    }
    
    setLocationLoading(true);
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude
        });
        setUseLocation(true);
        setLocationLoading(false);
      },
      (error) => {
        setLocationLoading(false);
        switch(error.code) {
          case error.PERMISSION_DENIED:
            showError('Location permission denied');
            break;
          case error.POSITION_UNAVAILABLE:
            showError('Location information is unavailable');
            break;
          case error.TIMEOUT:
            showError('Location request timed out');
            break;
          default:
            showError('An unknown error occurred when trying to get your location');
        }
      }
    );
  };
  
  // Toggle location-based search
  const toggleLocationSearch = () => {
    if (useLocation) {
      setUseLocation(false);
    } else {
      if (userLocation) {
        setUseLocation(true);
      } else {
        getLocation();
      }
    }
  };
  
  return (
    <div className="min-h-screen">
      {/* Hero Search Section */}
      <div className="bg-gradient-to-r from-primary to-primary-dark text-white py-16 px-4">
        <div className="container mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Find the Perfect School or Expert
          </h1>
          <p className="text-xl mb-10 max-w-2xl mx-auto">
            Search for top-rated schools and educational mentors to help you on your educational journey
          </p>
          
          {/* Search Form */}
          <div className="max-w-4xl mx-auto">
            <form onSubmit={handleSearch} className="relative">
              <div className="bg-white rounded-lg shadow-xl overflow-hidden flex">
                <div className="hidden md:flex md:items-center">
                  <div className="flex px-4 space-x-2">
                    <button
                      type="button"
                      className={`px-3 py-1 rounded-full text-sm ${searchType === 'all' ? 'bg-primary text-white' : 'text-gray-700 hover:bg-gray-100'}`}
                      onClick={() => handleSearchTypeChange('all')}
                    >
                      All
                    </button>
                    <button
                      type="button"
                      className={`px-3 py-1 rounded-full text-sm flex items-center ${searchType === 'schools' ? 'bg-primary text-white' : 'text-gray-700 hover:bg-gray-100'}`}
                      onClick={() => handleSearchTypeChange('schools')}
                    >
                      <FaSchool className="mr-1" size={12} /> Schools
                    </button>
                    <button
                      type="button"
                      className={`px-3 py-1 rounded-full text-sm flex items-center ${searchType === 'mentors' ? 'bg-primary text-white' : 'text-gray-700 hover:bg-gray-100'}`}
                      onClick={() => handleSearchTypeChange('mentors')}
                    >
                      <FaUserTie className="mr-1" size={12} /> Mentors
                    </button>
                  </div>
                </div>
                <div className="flex-grow relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaSearch className="text-gray-400" />
                  </div>
                  <input
                    type="text"
                    className="form-input w-full py-4 pl-10 pr-20 rounded-none text-gray-700"
                    placeholder="Search for schools or mentors..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                  />
                </div>
                <button
                  type="button"
                  onClick={toggleLocationSearch}
                  className={`hidden md:flex items-center px-4 border-l border-gray-300 ${useLocation ? 'text-primary font-medium' : 'text-gray-600'} ${locationLoading ? 'opacity-50 cursor-wait' : ''}`}
                  disabled={locationLoading}
                >
                  <FaMapMarkerAlt className={`mr-2 ${useLocation ? 'text-primary' : 'text-gray-500'}`} />
                  {locationLoading ? 'Getting location...' : (useLocation ? 'Using your location' : 'Near me')}
                </button>
                <button
                  type="submit"
                  className="bg-primary hover:bg-primary-dark text-white px-8 py-4 font-medium transition-colors"
                >
                  Search
                </button>
              </div>
              
              <div className="md:hidden mt-4 flex justify-center space-x-3">
                <button
                  type="button"
                  className={`px-3 py-1 rounded-full text-sm ${searchType === 'all' ? 'bg-white text-primary' : 'text-white bg-opacity-20 bg-white'}`}
                  onClick={() => handleSearchTypeChange('all')}
                >
                  All
                </button>
                <button
                  type="button"
                  className={`px-3 py-1 rounded-full text-sm flex items-center ${searchType === 'schools' ? 'bg-white text-primary' : 'text-white bg-opacity-20 bg-white'}`}
                  onClick={() => handleSearchTypeChange('schools')}
                >
                  <FaSchool className="mr-1" size={12} /> Schools
                </button>
                <button
                  type="button"
                  className={`px-3 py-1 rounded-full text-sm flex items-center ${searchType === 'mentors' ? 'bg-white text-primary' : 'text-white bg-opacity-20 bg-white'}`}
                  onClick={() => handleSearchTypeChange('mentors')}
                >
                  <FaUserTie className="mr-1" size={12} /> Mentors
                </button>
              </div>
              
              <div className="md:hidden mt-3">
                <button
                  type="button"
                  onClick={toggleLocationSearch}
                  className={`flex items-center mx-auto px-3 py-1 rounded-full text-sm ${useLocation ? 'bg-white text-primary' : 'text-white bg-opacity-20 bg-white'} ${locationLoading ? 'opacity-50 cursor-wait' : ''}`}
                  disabled={locationLoading}
                >
                  <FaMapMarkerAlt className="mr-1" size={12} />
                  {locationLoading ? 'Getting location...' : (useLocation ? 'Using your location' : 'Near me')}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      
      {/* Search Results */}
      {hasSearched && (
        <div className="container mx-auto px-4 py-8">
          {isSearching ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
              <p className="mt-4 text-gray-600">Searching...</p>
            </div>
          ) : (
            <div>
              {/* Results Tabs */}
              <div className="border-b border-gray-200 mb-6">
                <div className="flex space-x-8">
                  <button
                    className={`py-4 text-lg font-medium border-b-2 ${searchType === 'all' || searchType === 'schools' ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                    onClick={() => handleSearchTypeChange('schools')}
                  >
                    Schools
                  </button>
                  <button
                    className={`py-4 text-lg font-medium border-b-2 ${searchType === 'all' || searchType === 'mentors' ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                    onClick={() => handleSearchTypeChange('mentors')}
                  >
                    Mentors/Experts
                  </button>
                </div>
              </div>
              
              {/* Results List */}
              {(searchType === 'all' || searchType === 'schools') && (
                <SchoolResults
                  searchTerm={searchTerm}
                  useLocation={useLocation}
                  userLocation={userLocation}
                />
              )}
              
              {(searchType === 'all' || searchType === 'mentors') && (
                <MentorResults
                  searchTerm={searchTerm}
                  useLocation={useLocation}
                  userLocation={userLocation}
                />
              )}
            </div>
          )}
        </div>
      )}
      
      {/* Info Sections (shown when no search has been performed) */}
      {!hasSearched && (
        <div className="container mx-auto px-4 py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div className="bg-white rounded-xl shadow-soft p-8">
              <div className="flex items-center mb-6">
                <div className="bg-blue-100 p-3 rounded-full mr-4">
                  <FaSchool className="text-blue-600 text-2xl" />
                </div>
                <h2 className="text-2xl font-bold text-gray-800">Find the Best Schools</h2>
              </div>
              <p className="text-gray-600 mb-6">
                Discover top-rated schools in your area or anywhere in Kenya. Filter by curriculum, location, fees, and more to find the perfect educational institution for your children.
              </p>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  Compare fees, curriculum offerings and facilities
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  Read verified parent reviews and ratings
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  Find schools near your location
                </li>
              </ul>
            </div>
            
            <div className="bg-white rounded-xl shadow-soft p-8">
              <div className="flex items-center mb-6">
                <div className="bg-purple-100 p-3 rounded-full mr-4">
                  <FaUserTie className="text-purple-600 text-2xl" />
                </div>
                <h2 className="text-2xl font-bold text-gray-800">Connect with Experts</h2>
              </div>
              <p className="text-gray-600 mb-6">
                Connect with qualified mentors and educational experts who can provide personalized guidance and support for your educational journey.
              </p>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  Find mentors by subject expertise or specialty
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  Schedule sessions with experienced educators
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  Get personalized educational guidance
                </li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FindExpert; 